import React, { useCallback, useEffect, useRef, useState } from 'react';
import DashboardHeader from '@/components/Header/DashboardHeader';
import { Footer } from '@/components/organisms/Footer';
import { apiService } from '@/services/api';
import { subscribeToChannel } from '@/services/ablyRealtime';

/**
 * Branch Groups (member side) — the interest groups a branch invited me to. Accept an invite, then
 * chat with the branch and other members WhatsApp-style. Live via the branch_group:<id> channel.
 */

interface Group { groupId: string; name: string; subject?: string | null; branchName: string; membershipStatus: 'invited' | 'joined' }
interface Msg { id: string; senderType: 'branch' | 'customer'; senderName?: string | null; senderId?: string | null; text: string; createdAt: string }

const wrap: React.CSSProperties = { maxWidth: 720, margin: '0 auto', padding: '16px 14px 80px', color: 'var(--text-primary, #111827)' };
const card: React.CSSProperties = { background: 'var(--bg-secondary, #fff)', border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 14, padding: 14 };
const input: React.CSSProperties = { padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-color,#e5e7eb)', background: 'var(--bg-secondary,#fff)', color: 'var(--text-primary,#111827)' };
const btn: React.CSSProperties = { ...input, cursor: 'pointer', fontWeight: 700, background: 'var(--accent, #4f46e5)', color: '#fff', border: 'none' };

export default function BranchGroups() {
  const [groups, setGroups] = useState<Group[]>([]);
  const [sel, setSel] = useState<Group | null>(null);
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const mounted = useRef(true);
  const scroller = useRef<HTMLDivElement | null>(null);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    try { const g = await apiService.branchGroups(); if (mounted.current) setGroups(g); }
    catch (e: any) { if (mounted.current) setErr(e?.message || 'Could not load your groups'); }
    finally { if (mounted.current) setLoading(false); }
  }, []);

  useEffect(() => { mounted.current = true; loadGroups(); return () => { mounted.current = false; }; }, [loadGroups]);

  const openGroup = useCallback(async (g: Group) => {
    setSel(g); setMsgs([]); setErr('');
    if (g.membershipStatus !== 'joined') return;
    try { const m = await apiService.branchGroupMessages(g.groupId); if (mounted.current) { setMsgs(m); setTimeout(() => scroller.current?.scrollTo(0, scroller.current.scrollHeight), 30); } }
    catch (e: any) { if (mounted.current) setErr(e?.message || 'Could not load messages'); }
  }, []);

  // Live messages when a joined group is open.
  useEffect(() => {
    if (!sel || sel.membershipStatus !== 'joined') return;
    const unsub = subscribeToChannel(`branch_group:${sel.groupId}`, ['message:new'], (_e, m: any) => {
      setMsgs((prev) => prev.some((x) => x.id === m.id) ? prev : [...prev, m]);
      setTimeout(() => scroller.current?.scrollTo(0, scroller.current.scrollHeight), 30);
    });
    return () => { try { unsub(); } catch { /* noop */ } };
  }, [sel]);

  const accept = async (g: Group) => {
    if (busy) return; setBusy(true);
    try { await apiService.acceptBranchGroup(g.groupId); const upd = { ...g, membershipStatus: 'joined' as const }; if (mounted.current) { setSel(upd); setGroups((p) => p.map((x) => x.groupId === g.groupId ? upd : x)); await openGroup(upd); } }
    catch (e: any) { if (mounted.current) setErr(e?.message || 'Could not accept'); }
    finally { if (mounted.current) setBusy(false); }
  };
  const leave = async (g: Group) => {
    if (busy || !window.confirm(`Leave "${g.name}"?`)) return; setBusy(true);
    try { await apiService.leaveBranchGroup(g.groupId); if (mounted.current) { setSel(null); await loadGroups(); } }
    catch (e: any) { if (mounted.current) setErr(e?.message || 'Could not leave'); }
    finally { if (mounted.current) setBusy(false); }
  };
  const send = async () => {
    if (!sel || !draft.trim() || busy) return; const t = draft.trim(); setDraft(''); setBusy(true);
    try { await apiService.postBranchGroupMessage(sel.groupId, t); }
    catch (e: any) { if (mounted.current) setErr(e?.message || 'Could not send'); }
    finally { if (mounted.current) setBusy(false); }
  };

  return (
    <>
      <DashboardHeader />
      <div style={wrap}>
        {!sel ? (
          <>
            <h1 style={{ fontSize: 22, fontWeight: 800, margin: '4px 0 4px' }}>👥 My Groups</h1>
            <p style={{ color: 'var(--text-secondary,#6b7280)', fontSize: 13, marginTop: 0 }}>Interest groups your venues invited you to.</p>
            {err && <div style={{ ...card, borderColor: 'var(--danger,#dc2626)', color: 'var(--danger,#dc2626)', marginBottom: 10 }}>{err}</div>}
            {loading ? <div style={{ ...card, textAlign: 'center', color: 'var(--text-secondary,#6b7280)' }}>Loading…</div>
              : groups.length === 0 ? <div style={{ ...card, textAlign: 'center', color: 'var(--text-secondary,#6b7280)' }}>No group invites yet.</div>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {groups.map((g) => (
                    <div key={g.groupId} style={{ ...card, cursor: 'pointer', display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }} onClick={() => openGroup(g)}>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 700 }}>{g.name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)' }}>{g.branchName}{g.subject ? ` · ${g.subject}` : ''}</div>
                      </div>
                      {g.membershipStatus === 'invited' ? <span style={{ fontSize: 11, fontWeight: 800, color: 'var(--accent,#4f46e5)', border: '1px solid var(--accent,#4f46e5)', borderRadius: 999, padding: '2px 10px' }}>INVITED</span> : <span style={{ fontSize: 18 }}>›</span>}
                    </div>
                  ))}
                </div>}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <button onClick={() => { setSel(null); loadGroups(); }} style={{ ...input, cursor: 'pointer', fontWeight: 700 }}>← Back</button>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800 }}>{sel.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary,#6b7280)' }}>{sel.branchName}</div>
              </div>
              {sel.membershipStatus === 'joined' && <button onClick={() => leave(sel)} style={{ ...input, marginLeft: 'auto', cursor: 'pointer', color: 'var(--danger,#dc2626)', fontWeight: 700 }}>Leave</button>}
            </div>
            {err && <div style={{ ...card, borderColor: 'var(--danger,#dc2626)', color: 'var(--danger,#dc2626)', marginBottom: 10 }}>{err}</div>}

            {sel.membershipStatus === 'invited' ? (
              <div style={{ ...card, textAlign: 'center' }}>
                <p style={{ margin: '4px 0 12px' }}>{sel.branchName} invited you to <strong>{sel.name}</strong>{sel.subject ? ` — ${sel.subject}` : ''}.</p>
                <button style={btn} disabled={busy} onClick={() => accept(sel)}>Join group</button>
              </div>
            ) : (
              <div style={{ ...card, display: 'flex', flexDirection: 'column', height: '65vh', padding: 0 }}>
                <div ref={scroller} style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {msgs.length === 0 ? <div style={{ color: 'var(--text-secondary,#9ca3af)', fontSize: 13, margin: 'auto' }}>No messages yet.</div> : msgs.map((m) => (
                    <div key={m.id} style={{ alignSelf: m.senderType === 'customer' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary,#9ca3af)', marginBottom: 2, textAlign: m.senderType === 'customer' ? 'right' : 'left' }}>{m.senderType === 'branch' ? `🏠 ${sel.branchName}` : (m.senderName || 'You')}</div>
                      <div style={{ background: m.senderType === 'customer' ? 'var(--accent,#4f46e5)' : 'var(--bg-tertiary,#f3f4f6)', color: m.senderType === 'customer' ? '#fff' : 'var(--text-primary,#111827)', borderRadius: 12, padding: '8px 12px', fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{m.text}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid var(--border-color,#e5e7eb)' }}>
                  <input style={{ ...input, flex: 1 }} placeholder="Message…" value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} />
                  <button style={btn} disabled={busy || !draft.trim()} onClick={send}>Send</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
      <Footer />
    </>
  );
}
